import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react';

export interface FeatureContextValue {
  /** Checks whether a feature flag is enabled */
  hasFeature: (name: string) => boolean;
  /** Whether feature data has finished loading */
  isLoaded: boolean;
}

const FeatureContext = createContext<FeatureContextValue | undefined>(undefined);

export interface FeatureProviderProps {
  children: ReactNode;
  /** Function that checks whether a feature flag is enabled */
  hasFeature: (name: string) => boolean;
  /** Whether feature data has finished loading */
  isLoaded: boolean;
}

/**
 * Provides feature availability data to FeatureGuard and other consumers.
 * Must wrap components that use FeatureGuard or useFeatureContext.
 *
 * @example
 * <FeatureProvider hasFeature={(name) => enabledFlags[name] ?? false} isLoaded={true}>
 *   <App />
 * </FeatureProvider>
 */
export const FeatureProvider: FC<FeatureProviderProps> = ({
  children,
  hasFeature,
  isLoaded,
}) => {
  const value = useMemo(() => ({ hasFeature, isLoaded }), [hasFeature, isLoaded]);

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
};

FeatureProvider.displayName = 'FeatureProvider';

/**
 * Hook to access the current feature context.
 * Must be used within a FeatureProvider.
 *
 * @throws Error if used outside of a FeatureProvider
 */
export const useFeatureContext = (): FeatureContextValue => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatureContext must be used within a FeatureProvider');
  }
  return context;
};
